import React, { Suspense, useEffect, useState, useContext } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route as RouterRoute, Navigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import moment from "moment";

import LoggedInLayout from "../layout";
import BackdropLoading from "../components/BackdropLoading";
import LoadingSplash from "../components/LoadingSplash";
import { AuthProvider, AuthContext } from "../context/Auth/AuthContext";
import PushNotificationManager from "../components/PushNotificationManager";
import { TicketsContextProvider } from "../context/Tickets/TicketsContext";
import { WhatsAppsProvider } from "../context/WhatsApp/WhatsAppsContext";
import Route from "./Route";
// — Páginas ligeras: carga estática (ruta crítica) —
const TicketResponsiveContainer = React.lazy(() => import("../pages/TicketResponsiveContainer"));
const Signup = React.lazy(() => import("../pages/Signup"));
const OnboardingWizard = React.lazy(() => import("../pages/Onboarding"));
const Login = React.lazy(() => import("../pages/Login/"));
// PUNTO3-8 (compartir vista previa del flujo por link -- orden
// definitivo de Edmundo 2026-11-04): pagina PUBLICA real, sin login --
// declarada afuera de <PrivateLayout> mas abajo, igual que /login.
const FlowPreviewPublic = React.lazy(() => import("../pages/FlowPreviewPublic"));
const ForgotPassword = React.lazy(() => import("../pages/ForgotPassword"));
const ResetPassword = React.lazy(() => import("../pages/ResetPassword"));
const Connections = React.lazy(() => import("../pages/Connections/"));
const Settings = React.lazy(() => import("../pages/Settings/"));
const NotificationSettings = React.lazy(() => import("../pages/NotificationSettings"));
const Financeiro = React.lazy(() => import("../pages/Financeiro/"));
const PlanCheckoutProviders = React.lazy(() => import("../pages/PlanCheckoutProviders"));
const Users = React.lazy(() => import("../pages/Users"));
const Contacts = React.lazy(() => import("../pages/ContactsActivity/"));
const Marketing = React.lazy(() => import("../pages/Marketing/"));
const WhatsappUsage = React.lazy(() => import("../pages/WhatsappUsage/"));
const MarketingNewCampaign = React.lazy(() => import("../pages/Marketing/NewCampaign/"));
const ContactImportPage = React.lazy(() => import("../pages/Contacts/import"));
const Queues = React.lazy(() => import("../pages/Queues/"));
const CustomFields = React.lazy(() => import("../pages/CustomFields/"));
const Flujos = React.lazy(() => import("../pages/Flujos/"));
const ShessaiIAPage = React.lazy(() => import("../pages/ShessaiIA/"));
const AgentesIAPage = React.lazy(() => import("../pages/AgentesIA/"));
// GRUPO A, A1 (pedido directo de Edmundo 2026-11-04): asistente de
// arranque guiado para negocios piloto nuevos.
const PilotOnboardingWizard = React.lazy(() => import("../pages/PilotOnboardingWizard"));
const TuDiaPage = React.lazy(() => import("../pages/TuDia/"));
const ConfiguracionIAPage = React.lazy(() => import("../pages/ConfiguracionIA/"));
const FlowDatabaseView = React.lazy(() => import("../pages/FlowDatabaseView/"));
const Tags = React.lazy(() => import("../pages/Tags/"));
const MessagesAPI = React.lazy(() => import("../pages/MessagesAPI/"));
const Helps = React.lazy(() => import("../pages/Helps/"));
const PerfilPage = React.lazy(() => import("../pages/PerfilPage"));
const Ayuda = React.lazy(() => import("../pages/Ayuda/"));
const Wallets = React.lazy(() => import("../pages/Wallets/"));
const QuickMessages = React.lazy(() => import("../pages/QuickMessages/"));
const Schedules = React.lazy(() => import("../pages/Schedules"));
const Annoucements = React.lazy(() => import("../pages/Annoucements"));
const Prompts = React.lazy(() => import("../pages/Prompts"));
const AllConnections = React.lazy(() => import("../pages/AllConnections/"));
const QueueIntegration = React.lazy(() => import("../pages/QueueIntegration"));
const Files = React.lazy(() => import("../pages/Files/"));
const ToDoList = React.lazy(() => import("../pages/ToDoList/"));
const BirthdaySettingsPage = React.lazy(() => import("../pages/BirthdaySettings"));
const CallHistoricals = React.lazy(() => import("../pages/CallHistoricals"));
const Companies = React.lazy(() => import("../pages/Companies/"));
const CompanyDetails = React.lazy(() => import("../pages/CompanyDetails"));
const SSOLogout = React.lazy(() => import("../pages/SSOLogout"));
const WhatsAppCloudApiTest = React.lazy(() => import("../pages/WhatsAppCloudApiTest"));
const WhatsappTemplates = React.lazy(() => import("../pages/WhatsappTemplates"));
const LlamadasIA = React.lazy(() => import("../pages/LlamadasIA"));

const PageSkeleton = () => (
  <div style={{ padding: '20px', animation: 'fadeIn 0.3s ease' }}>
    <div className="skeleton" style={{ height: 44, marginBottom: 16, borderRadius: "8px" }} />
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
      <div className="skeleton" style={{ height: 72, borderRadius: "10px" }} />
      <div className="skeleton" style={{ height: 72, borderRadius: "10px" }} />
      <div className="skeleton" style={{ height: 72, borderRadius: "10px" }} />
    </div>
    <div className="skeleton" style={{ height: 400, borderRadius: "10px" }} />
  </div>
);



// — Páginas pesadas: lazy loading (xlsx / ace-builds / html2pdf / heavy UI) —
const Dashboard = React.lazy(() => import("../pages/Dashboard/"));
const Reports = React.lazy(() => import("../pages/Reports"));
const RelatorioVendas = React.lazy(() => import("../pages/RelatorioVendas"));
const CampaignReport = React.lazy(() => import("../pages/CampaignReport"));
const Campaigns = React.lazy(() => import("../pages/Campaigns"));
const CampaignsConfig = React.lazy(() => import("../pages/CampaignsConfig"));
const CampaignsPhrase = React.lazy(() => import("../pages/CampaignsPhrase"));
const ContactLists = React.lazy(() => import("../pages/ContactLists/"));
const ContactListItems = React.lazy(() => import("../pages/ContactListItems/"));
const Kanban = React.lazy(() => import("../pages/Kanban"));
const TagsKanban = React.lazy(() => import("../pages/TagsKanban"));
const Chat = React.lazy(() => import("../pages/Chat"));
const ChatMoments = React.lazy(() => import("../pages/Moments"));
const StatusFeed = React.lazy(() => import("../pages/StatusFeed"));
const FlowBuilder = React.lazy(() => import("../pages/FlowBuilder"));
const FlowDefault = React.lazy(() => import("../pages/FlowDefault"));
const FlowBuilderConfig = React.lazy(() =>
  import("../pages/FlowBuilderConfig").then(m => ({ default: m.FlowBuilderConfig }))
);

const PrivateLayout = () => {
  const { user } = useContext(AuthContext);

  const isCompanyExpired = () => {
    if (!user || !user.company || user.company.id === 1) return false;
    const dueDate = user.company.dueDate;
    if (!dueDate) return false;
    const hojeInicio = moment().startOf("day");
    const vencimentoInicio = moment(dueDate).startOf("day");
    return hojeInicio.isAfter(vencimentoInicio, "day");
  };

  return (
    <WhatsAppsProvider>
      <LoggedInLayout hideMenu={isCompanyExpired()} />
    </WhatsAppsProvider>
  );
};

const RoutesContent = () => {
  const [showCampaigns, setShowCampaigns] = useState(false);

  useEffect(() => {
    const cshow = localStorage.getItem("cshow");
    if (cshow !== undefined) setShowCampaigns(true);
  }, []);

  return (
    <>
      <PushNotificationManager />
      <TicketsContextProvider>
      <Suspense fallback={<LoadingSplash label="Cargando..." />}>
        <RouterRoutes>
          <RouterRoute path="/login" element={<Route element={<Login />} />} />
          <RouterRoute path="/flow-preview/:token" element={<Route element={<FlowPreviewPublic />} />} />
          <RouterRoute path="/forgot-password" element={<Route element={<ForgotPassword />} />} />
          <RouterRoute path="/reset-password" element={<Route element={<ResetPassword />} />} />
          <RouterRoute path="/signup" element={<Route element={<Signup />} />} />
          <RouterRoute path="/onboarding" element={<Route element={<OnboardingWizard />} isPrivate path="/onboarding" />} />
          <RouterRoute path="/sso-logout" element={<SSOLogout />} />

          <RouterRoute element={<PrivateLayout />}>
            <RouterRoute path="/" element={<Route element={<Dashboard />} isPrivate path="/" />} />
            <RouterRoute path="/call-historicals" element={<Route element={<CallHistoricals />} isPrivate path="/call-historicals" />} />
            <RouterRoute path="/tickets/:ticketId?" element={<Route element={<TicketResponsiveContainer />} isPrivate path="/tickets/:ticketId?" />} />
            <RouterRoute path="/financeiro" element={<Route element={<Financeiro />} isPrivate path="/financeiro" />} />
            <RouterRoute path="/financeiro-aberto" element={<Route element={<Financeiro />} isPrivate path="/financeiro-aberto" />} />
            <RouterRoute path="/plan-checkout-providers" element={<Route element={<PlanCheckoutProviders />} isPrivate path="/plan-checkout-providers" />} />
            <RouterRoute path="/companies" element={<Route element={<Companies />} isPrivate path="/companies" />} />
            <RouterRoute path="/whatsapp-templates" element={<Route element={<WhatsappTemplates />} isPrivate path="/whatsapp-templates" />} />
            <RouterRoute path="/companies/:companyId" element={<Route element={<CompanyDetails />} isPrivate path="/companies/:companyId" />} />
            <RouterRoute path="/birthday-settings" element={<Route element={<BirthdaySettingsPage />} isPrivate path="/birthday-settings" />} />
            <RouterRoute path="/connections" element={<Route element={<Connections />} isPrivate path="/connections" />} />
            <RouterRoute path="/quick-messages" element={<Route element={<QuickMessages />} isPrivate path="/quick-messages" />} />
            <RouterRoute path="/todolist" element={<Route element={<ToDoList />} isPrivate path="/todolist" />} />
            <RouterRoute path="/schedules" element={<Route element={<Schedules />} isPrivate path="/schedules" />} />
            <RouterRoute path="/tags" element={<Route element={<Tags />} isPrivate path="/tags" />} />
            <RouterRoute path="/contacts" element={<Route element={<Contacts />} isPrivate path="/contacts" />} />
            <RouterRoute path="/contacts/import" element={<Route element={<ContactImportPage />} isPrivate path="/contacts/import" />} />
            <RouterRoute path="/marketing" element={<Route element={<Marketing />} isPrivate path="/marketing" />} />
            <RouterRoute path="/marketing/new" element={<Route element={<MarketingNewCampaign />} isPrivate path="/marketing/new" />} />
            <RouterRoute path="/whatsapp-usage" element={<Route element={<WhatsappUsage />} isPrivate path="/whatsapp-usage" />} />
            <RouterRoute path="/wallets" element={<Route element={<Wallets />} isPrivate path="/wallets" />} />
            <RouterRoute path="/helps" element={<Route element={<Helps />} isPrivate path="/helps" />} />
            {/* Ver TAREA: centro de ayuda del CRM (Fase 4). Convive con /helps
                por ahora -- ver reporte de la decisión punto 8 antes de tocar
                esa ruta. */}
            <RouterRoute path="/ayuda" element={<Route element={<Ayuda />} isPrivate path="/ayuda" />} />
            {/* Ver TAREA: pantalla Perfil + reorganización de la barra
                inferior (mockup aprobado). Reemplaza a "Más" como el
                último tab de la barra. */}
            <RouterRoute path="/perfil" element={<Route element={<PerfilPage />} isPrivate path="/perfil" />} />
            <RouterRoute path="/users" element={<Route element={<Users />} isPrivate path="/users" />} />
            <RouterRoute path="/messages-api" element={<Route element={<MessagesAPI />} isPrivate path="/messages-api" />} />
            <RouterRoute path="/notifications" element={<Route element={<NotificationSettings />} isPrivate path="/notifications" />} />
            <RouterRoute path="/settings" element={<Route element={<Settings />} isPrivate path="/settings" />} />
            <RouterRoute path="/queues" element={<Route element={<Queues />} isPrivate path="/queues" />} />
            <RouterRoute path="/custom-fields" element={<Route element={<CustomFields />} isPrivate path="/custom-fields" />} />
            <RouterRoute path="/flujos" element={<Route element={<Flujos />} isPrivate path="/flujos" />} />
            {/* Unificacion 2026-10-26 (decision del usuario): el agente de
                WhatsApp dejo de tener pantalla propia -- /shessai-ia (y
                sus tabs viejas por ?tab=...) redirige a la pantalla nueva
                dentro de "Agentes IA". Se redirige, no se borra la ruta ni
                el componente viejo (ShessaiIAPage queda importado pero sin
                usar). */}
            <RouterRoute path="/shessai-ia" element={<Route element={<Navigate to="/agentes-ia/whatsapp" replace />} isPrivate path="/shessai-ia" />} />
            <RouterRoute path="/agentes-ia" element={<Route element={<AgentesIAPage />} isPrivate path="/agentes-ia" />} />
            <RouterRoute path="/asistente-arranque" element={<Route element={<PilotOnboardingWizard />} isPrivate path="/asistente-arranque" />} />
            <RouterRoute path="/agentes-ia/whatsapp" element={<Route element={<AgentesIAPage />} isPrivate path="/agentes-ia/whatsapp" />} />
            <RouterRoute path="/tu-dia" element={<Route element={<TuDiaPage />} isPrivate path="/tu-dia" />} />
            <RouterRoute path="/configuracion-ia" element={<Route element={<ConfiguracionIAPage />} isPrivate path="/configuracion-ia" />} />
            <RouterRoute path="/flow-databases/:id" element={<Route element={<FlowDatabaseView />} isPrivate path="/flow-databases/:id" />} />
            <RouterRoute path="/reports" element={<Route element={<Reports />} isPrivate path="/reports" />} />
            <RouterRoute path="/relatorio-vendas" element={<Route element={<RelatorioVendas />} isPrivate path="/relatorio-vendas" />} />
            <RouterRoute path="/queue-integration" element={<Route element={<QueueIntegration />} isPrivate path="/queue-integration" />} />
            <RouterRoute path="/announcements" element={<Route element={<Annoucements />} isPrivate path="/announcements" />} />
            <RouterRoute path="/chats/:id?" element={<Route element={<Chat />} isPrivate path="/chats/:id?" />} />
            <RouterRoute path="/files" element={<Route element={<Files />} isPrivate path="/files" />} />
            <RouterRoute path="/moments" element={<Route element={<ChatMoments />} isPrivate path="/moments" />} />
            <RouterRoute path="/statuses" element={<Route element={<StatusFeed />} isPrivate path="/statuses" />} />
            <RouterRoute path="/Kanban" element={<Route element={<Kanban />} isPrivate path="/Kanban" />} />
            <RouterRoute path="/TagsKanban" element={<Route element={<TagsKanban />} isPrivate path="/TagsKanban" />} />
            <RouterRoute path="/prompts" element={<Route element={<Prompts />} isPrivate path="/prompts" />} />
            <RouterRoute path="/allConnections" element={<Route element={<AllConnections />} isPrivate path="/allConnections" />} />
            <RouterRoute path="/phrase-lists" element={<Route element={<CampaignsPhrase />} isPrivate path="/phrase-lists" />} />
            <RouterRoute path="/flowbuilders" element={<Route element={<FlowBuilder />} isPrivate path="/flowbuilders" />} />
            <RouterRoute path="/flowbuilder/:id?" element={<Route element={<FlowBuilderConfig />} isPrivate path="/flowbuilder/:id?" />} />
            <RouterRoute path="/test/whatsapp-cloud-api" element={<Route element={<WhatsAppCloudApiTest />} isPrivate path="/test/whatsapp-cloud-api" />} />
            <RouterRoute path="/llamadas-ia" element={<Route element={<LlamadasIA />} isPrivate path="/llamadas-ia" />} />
            {showCampaigns && (
              <>
                <RouterRoute path="/contact-lists" element={<Route element={<ContactLists />} isPrivate path="/contact-lists" />} />
                <RouterRoute path="/contact-lists/:contactListId/contacts" element={<Route element={<ContactListItems />} isPrivate path="/contact-lists/:contactListId/contacts" />} />
                <RouterRoute path="/campaigns" element={<Route element={<Campaigns />} isPrivate path="/campaigns" />} />
                <RouterRoute path="/campaign/:campaignId/report" element={<Route element={<CampaignReport />} isPrivate path="/campaign/:campaignId/report" />} />
                <RouterRoute path="/campaigns-config" element={<Route element={<CampaignsConfig />} isPrivate path="/campaigns-config" />} />
              </>
            )}
          </RouterRoute>
        </RouterRoutes>
      </Suspense>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        toastStyle={{
          borderRadius: "10px",
          fontSize: "13px",
          fontWeight: 500,
          fontFamily: "Inter, sans-serif",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          border: "0.5px solid rgba(0,0,0,0.08)",
          padding: "12px 16px",
        }}
        style={{ top: "16px", right: "16px" }}
      />
    </TicketsContextProvider>
    </>
  );
};

const Routes = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <RoutesContent />
      </AuthProvider>
    </BrowserRouter>
  );
};

export default Routes;
